
import React, { useState, useEffect } from 'react';
import { useAdminPage } from '@/hooks/useAdminPage';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminStats from './AdminStats';
import AttendeesContent from './AttendeesContent';
import ActivityLog from './ActivityLog';
import { Info, Users, ClipboardList, Shirt, ChevronDown, ChevronUp } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useIsMobile } from '@/hooks/use-mobile';
import FloatingActionButton from './FloatingActionButton';
import { CameraQrScanner } from './scanner';
import AdminTShirtStats from './stats/AdminTShirtStats';

interface AdminContentProps {
  onRefreshData?: () => void;
  onDirectUpdate?: (code: string, updates: any) => Promise<any>;
}

const AdminContent: React.FC<AdminContentProps> = ({ 
  onRefreshData,
  onDirectUpdate
}) => {
  const {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    attendees,
    adminActions,
    adminState,
    totalRegistered,
    totalAttended,
    totalContribution,
    isLoading,
    isActionThrottled,
    handleQrScan,
    handleCodeSubmit
  } = useAdminPage();
  
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [showDetailedTShirtStats, setShowDetailedTShirtStats] = useState(false);
  const [lastSearches, setLastSearches] = useState<string[]>([]);
  const isMobile = useIsMobile();
  
  const handleQrDetected = (code: string) => {
    handleQrScan(code);
    setShowQrScanner(false);
  };

  useEffect(() => {
    const savedSearches = localStorage.getItem('admin_last_searches');
    if (savedSearches) {
      try {
        const parsed = JSON.parse(savedSearches);
        if (Array.isArray(parsed)) {
          setLastSearches(parsed.slice(0, 5));
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }
    }
  }, []);

  useEffect(() => {
    if (searchQuery && searchQuery.trim().length >= 3) {
      const timeoutId = setTimeout(() => {
        setLastSearches(prev => {
          const updatedSearches = [searchQuery, ...prev.filter(s => s !== searchQuery)].slice(0, 5);
          localStorage.setItem('admin_last_searches', JSON.stringify(updatedSearches));
          return updatedSearches;
        });
      }, 2000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery]);

  const attendancePercentage = totalRegistered > 0 ? (totalAttended / totalRegistered) * 100 : 0;

  const handleTShirtStatsToggle = () => {
    setShowDetailedTShirtStats(prev => !prev);
  };

  return (
    <div className="space-y-5">
      <AdminStats 
        totalRegistered={totalRegistered} 
        totalAttended={totalAttended} 
        totalContribution={totalContribution}
        attendancePercentage={attendancePercentage}
        tShirtStats={attendees.length > 0 ? { ...useAdminPage().tShirtStats } : { ...useAdminPage().tShirtStats }}
        loading={isLoading}
      />
      
      {showDetailedTShirtStats && (
        <AdminTShirtStats attendees={attendees} className="mb-4" />
      )}
      
      <div className="flex justify-end">
        <button
          onClick={handleTShirtStatsToggle}
          className="bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-full transition-all duration-200 flex items-center gap-1.5 shadow-sm border border-blue-200 group relative z-10"
        >
          <Shirt size={16} className="text-blue-500 group-hover:text-blue-700" />
          <span className="text-xs font-medium">
            {showDetailedTShirtStats ? 'Ẩn chi tiết' : 'Xem chi tiết'} áo Kỷ Niệm
          </span>
          {showDetailedTShirtStats ? (
            <ChevronUp size={14} className="text-blue-500" />
          ) : (
            <ChevronDown size={14} className="text-blue-500" />
          )}
        </button>
      </div>
      
      <div className="relative z-10">
        <Tabs 
          defaultValue={activeTab} 
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="relative z-20 w-full mb-4 md:mb-6 grid grid-cols-2 max-w-[160px] md:max-w-[200px] mx-auto bg-white dark:bg-gray-800">
            <TabsTrigger 
              value="attendees" 
              className="rounded-md flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 relative z-20 data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-900/30"
              title="Thông tin người tham dự"
            >
              <Users size={isMobile ? 16 : 18} />
              <span className="sr-only md:not-sr-only md:inline">Người tham dự</span>
            </TabsTrigger>
            <TabsTrigger 
              value="activities" 
              className="rounded-md flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 relative z-20 data-[state=active]:bg-blue-50 dark:data-[state=active]:bg-blue-900/30"
              title="Lịch sử hoạt động"
            >
              <ClipboardList size={isMobile ? 16 : 18} />
              <span className="sr-only md:not-sr-only md:inline">Hoạt động</span>
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="attendees" className="relative z-10">
            <AttendeesContent
              totalAttended={totalAttended}
              totalRegistered={totalRegistered}
              attendees={attendees}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              onQrScan={handleQrScan}
              onCodeSubmit={handleCodeSubmit}
              onRefreshData={onRefreshData}
              onDirectUpdate={onDirectUpdate}
              onSilentUpdate={(attendee) => {
                // This is just a pass-through to handle silent updates
              }}
              lastSearches={lastSearches}
            />
          </TabsContent>
          
          <TabsContent value="activities" className="relative z-10">
            {isActionThrottled && (
              <Alert className="bg-amber-50 text-amber-800 border-amber-200 mb-4">
                <Info className="h-4 w-4 text-amber-600" />
                <AlertTitle>Chế độ tiết kiệm dữ liệu</AlertTitle>
                <AlertDescription>
                  Lịch sử hoạt động trong chế độ giới hạn để tối ưu hiệu năng. Chỉ hiển thị dữ liệu đã được lưu trữ.
                </AlertDescription>
              </Alert>
            )}
            
            <ActivityLog 
              adminActions={adminActions} 
              isLoading={isLoading} 
              activeAdmins={adminState?.activeAdmins || []}
            />
          </TabsContent>
        </Tabs>
      </div>
      
      {isMobile && (
        <FloatingActionButton onOpenQrScanner={() => setShowQrScanner(true)} />
      )}
      
      {showQrScanner && (
        <CameraQrScanner 
          onQrDetected={handleQrDetected}
          onClose={() => setShowQrScanner(false)}
        />
      )}
    </div>
  );
};

export default AdminContent;
